import { useEffect, useRef, useState } from 'react';
import { getMediaViewUrl, uploadImage } from '../services/mediaService';
import { createRestaurant, updateRestaurant } from '../services/restaurantService';

const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];

const pinIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const uploadIcon = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
    <path d="M12 18v-6" />
    <path d="M9 15l3-3 3 3" />
  </svg>
);

const initialForm = {
  name: '',
  location: '',
  description: '',
  status: 'active',
};

const MODE_COPY = {
  add: {
    title: 'Add Partner Venue',
    subtitle: 'Create a new listing in your network',
    submit: 'Save Partner Venue',
  },
  edit: {
    title: 'Edit Partner Venue',
    subtitle: 'Update listing details in your network',
    submit: 'Update Partner Venue',
  },
  view: {
    title: 'View Partner Venue',
    subtitle: 'Partner venue details in your network',
    submit: 'Edit',
  },
};

function revokeIfBlob(url) {
  if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
}

export default function AddRestaurantModal({
  open,
  onClose,
  onCreated,
  restaurant = null,
  mode: initialMode = 'add',
}) {
  const fileInputRef = useRef(null);
  const uploadIdRef = useRef(0);
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState(initialForm);
  const [imagePreview, setImagePreview] = useState('');
  const [imageKey, setImageKey] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isReadOnly = mode === 'view';
  const isEditing = mode === 'edit';
  const copy = MODE_COPY[mode] || MODE_COPY.add;

  useEffect(() => {
    if (!open) return;
    uploadIdRef.current += 1;
    setMode(initialMode);

    if (restaurant) {
      setForm({
        name: restaurant.name || '',
        location: restaurant.location || restaurant.address || '',
        description: restaurant.description || '',
        status: restaurant.status === 'inactive' ? 'inactive' : 'active',
      });
      setImagePreview(restaurant.imageUrl || '');
    } else {
      setForm(initialForm);
      setImagePreview('');
    }

    setImageKey('');
    setUploading(false);
    setError('');
    setSaving(false);
  }, [open, restaurant, initialMode]);

  useEffect(() => {
    return () => {
      revokeIfBlob(imagePreview);
    };
  }, [imagePreview]);

  if (!open) return null;

  function updateField(field, value) {
    if (isReadOnly) return;
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleImageChange(e) {
    if (isReadOnly) return;

    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError('Please upload a PNG or JPG image.');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setError('Image must be 10MB or smaller.');
      return;
    }

    revokeIfBlob(imagePreview);
    const localPreview = URL.createObjectURL(file);
    const uploadId = ++uploadIdRef.current;

    // Show local preview immediately while upload runs
    setImagePreview(localPreview);
    setImageKey('');
    setError('');
    setUploading(true);

    try {
      const token = localStorage.getItem('access_token');
      const { key } = await uploadImage(file, token);
      if (uploadId !== uploadIdRef.current) return;

      setImageKey(key);

      // Prefer a viewable GET URL for the uploaded object; keep local blob if unavailable
      const viewUrl = await getMediaViewUrl(key, token);
      if (uploadId !== uploadIdRef.current) return;

      if (viewUrl) {
        revokeIfBlob(localPreview);
        setImagePreview(viewUrl);
      }
    } catch (err) {
      if (uploadId !== uploadIdRef.current) return;
      revokeIfBlob(localPreview);
      setImagePreview(restaurant?.imageUrl || '');
      setImageKey('');
      setError(err.message || 'Failed to upload image. Please try again.');
    } finally {
      if (uploadId === uploadIdRef.current) {
        setUploading(false);
      }
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (isReadOnly) {
      setMode('edit');
      setError('');
      return;
    }

    const name = form.name.trim();
    const location = form.location.trim();

    if (!name || !location) {
      setError('Partner venue name and location are required.');
      return;
    }

    if (uploading) {
      setError('Please wait for the image upload to finish.');
      return;
    }

    setSaving(true);
    setError('');

    try {
      // imageUrl = S3 key frompresigned response (data.key), not the temporary upload URL
      const payload = {
        name,
        location,
        status: form.status === 'inactive' ? 'inactive' : 'active',
        description: form.description.trim() || undefined,
        ...(imageKey ? { imageUrl: imageKey } : {}),
      };

      const response = isEditing
        ? await updateRestaurant(restaurant.id, payload)
        : await createRestaurant(payload);

      onCreated?.(response.data || response);
      onClose();
    } catch (err) {
      setError(err.message || (isEditing ? 'Failed to update partner venue.' : 'Failed to save partner venue.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className={`add-restaurant-modal ${isReadOnly ? 'is-readonly' : ''}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-restaurant-title"
      >
        <div className="add-restaurant-modal-header">
          <div>
            <h2 id="add-restaurant-title">{copy.title}</h2>
            <p>{copy.subtitle}</p>
          </div>
          <button type="button" className="add-restaurant-close" onClick={onClose} aria-label="Close">
            &#x2715;
          </button>
        </div>

        <form className="add-restaurant-form" onSubmit={handleSubmit}>
          <div className="add-restaurant-row">
            <label className="add-restaurant-field">
              <span>Partner Venue Name</span>
              <input
                type="text"
                placeholder="e.g. Amber & Rye"
                value={form.name}
                onChange={(e) => updateField('name', e.target.value)}
                maxLength={150}
                required={!isReadOnly}
                disabled={isReadOnly}
                readOnly={isReadOnly}
              />
            </label>

            <label className="add-restaurant-field">
              <span>Location</span>
              <div className="add-restaurant-location-input">
                {pinIcon}
                <input
                  type="text"
                  placeholder="Start typing a address"
                  value={form.location}
                  onChange={(e) => updateField('location', e.target.value)}
                  maxLength={150}
                  required={!isReadOnly}
                  disabled={isReadOnly}
                  readOnly={isReadOnly}
                />
              </div>
            </label>
          </div>

          <div className="add-restaurant-field">
            <span>Partner Venue Image</span>
            <button
              type="button"
              className={`add-restaurant-upload ${imagePreview ? 'has-preview' : ''} ${uploading ? 'is-uploading' : ''} ${isReadOnly ? 'is-readonly' : ''}`}
              onClick={() => !isReadOnly && !uploading && fileInputRef.current?.click()}
              disabled={isReadOnly || uploading}
            >
              {imagePreview ? (
                <>
                  <img src={imagePreview} alt="Partner venue preview" />
                  {uploading && (
                    <span className="add-restaurant-upload-status">
                      <span className="add-restaurant-upload-spinner" aria-hidden="true" />
                      <span>Uploading...</span>
                    </span>
                  )}
                </>
              ) : uploading ? (
                <>
                  <span className="add-restaurant-upload-spinner" aria-hidden="true" />
                  <strong>Uploading...</strong>
                  <em>Please wait</em>
                </>
              ) : (
                <>
                  {uploadIcon}
                  <strong>{isReadOnly ? 'No image uploaded' : 'Click to upload an image'}</strong>
                  {!isReadOnly && <em>PNG or JPG, up to 10MB</em>}
                </>
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg"
              hidden
              disabled={isReadOnly}
              onChange={handleImageChange}
            />
          </div>

          <label className="add-restaurant-field">
            <span>Description</span>
            <textarea
              placeholder="A short, appetizing summary of the partner venue...."
              value={form.description}
              onChange={(e) => updateField('description', e.target.value)}
              maxLength={5000}
              rows={3}
              disabled={isReadOnly}
              readOnly={isReadOnly}
            />
          </label>

          <div className="add-restaurant-field">
            <span>Status</span>
            <div className="add-restaurant-status-radios" role="radiogroup" aria-label="Partner venue status">
              <label className={`add-restaurant-status-option ${form.status === 'active' ? 'is-selected' : ''}`}>
                <input
                  type="radio"
                  name="restaurant-status"
                  value="active"
                  checked={form.status === 'active'}
                  onChange={() => updateField('status', 'active')}
                  disabled={isReadOnly}
                />
                Active
              </label>
              <label className={`add-restaurant-status-option ${form.status === 'inactive' ? 'is-selected' : ''}`}>
                <input
                  type="radio"
                  name="restaurant-status"
                  value="inactive"
                  checked={form.status === 'inactive'}
                  onChange={() => updateField('status', 'inactive')}
                  disabled={isReadOnly}
                />
                Inactive
              </label>
            </div>
          </div>

          {error && <div className="add-restaurant-error">{error}</div>}

          <div className="add-restaurant-footer">
            <button
              type="submit"
              className="add-restaurant-save"
              disabled={!isReadOnly && (saving || uploading)}
            >
              {!isReadOnly && (saving || uploading) && (
                <span className="add-restaurant-btn-spinner" aria-hidden="true" />
              )}
              {isReadOnly
                ? 'Edit'
                : saving
                  ? 'Saving...'
                  : uploading
                    ? 'Uploading image...'
                    : copy.submit}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
